import React, {useEffect} from "react";
import {BrowserRouter as Router, Navigate, Route, Routes as Switch, useLocation} from "react-router-dom";
import HomeComponent from "../page/default/home/home.component";
import LoginComponent from "../page/default/login/login.component";
import {AuthContext, AuthProvider, useAuth} from "../contexts/auth-context";
import {PermissionsProvider} from "../contexts/permissions/permissions-context";
import {usePermission} from "../contexts/permissions/use-permission";
import LayoutComponent from "../components/layout/layout.component";
import Loading from "../components/loading/loading";
import PoMonitoringComponent from "../page/basic/po-monitoring/po-monitoring.component";
import PoMonitoringDetailComponent from "../page/basic/po-monitoring/po-monitoring-detail.component";
import ImportPoComponent from "../page/basic/import/import-po.component";
import ImportAmortizationComponent from "../page/basic/import/import-amortization.component";
import AssetControlComponent from "../page/basic/report/asset-control.component";
import AssetControlInvoiceComponent from "../page/basic/report/asset-control-invoice.component";
import ImportRelatedCostComponent from "../page/basic/import/import-related-cost.component";
import RelatedCostComponent from "../page/report/related-cost/related-cost.component";
import CostAllocationProjectCategoryComponent
    from "../page/basic/cost-allocation-project-category/cost-allocation-project-category.component";
import StockAndMaterialOutputComponent
    from "../page/basic/stock-and-material-output/stock-and-material-output.component";
import FTBReportComponent from "../page/basic/ftb-report/ftb-report.component";
import FtBomGroupComponent from "../page/basic/ft-bom-group/ft-bom-group";
import DashboardComponent from "../page/dashboard/dashboard.component";
import { URLS } from "../services/routes";
import MountJigaRmaComponent from "../page/basic/ftb-report/mount-jiga-rma.component";
import ReplenishmentComponent from "../page/replenishment/replenishment.component";
import ClientComponent from "../page/client/client.component";
import UserComponent from "../page/account/user/user.component";
import UserItemComponent from "../page/account/user/user-item/user-item.component";
import {Permission, Profile} from "../models/account/user";
import PendingNreInvoiceReportComponent from "../page/basic/report/pending-nre-invoice-report.component";
import DeniedAccess from "../page/default/denied-access/denied-access";
import ImportIntegrationRoutinesComponent from "../page/basic/import/import-integration-routines.component";
import ImportConfirmationMonitoringPo from "../page/basic/import/import-confirmation-monitoring-po";
import NpiHomeComponent from "../page/modules/npi/page/home/npi-home.component";
import MapLogHomeComponent from "../page/modules/maplog/page/home/maplog-home.component";
import AccessDeniedComponent from "../page/default/access-denied/access-denied.component";
import CategoryComponent from "../page/category/category.component";
import ProjectsComponent from "../page/projects/projects.component";
import NpiTemplateComponent from "../page/modules/npi/page/configuration/template/npi-template.component";
import NpiTemplateDetailComponent
    from "../page/modules/npi/page/configuration/template/npi-template-detail.component";
import NpiCalendarComponent from "../page/modules/npi/page/configuration/calendar/npi-calendar.component";
import NpiDashboardComponent from "../page/modules/npi/page/dashboard/npi-dashboard.component";
import NpiChecklistComponent from "../page/modules/npi/page/dashboard/npi-checklist.component";
import NpiProjectPlanningComponent from "../page/modules/npi/page/project-planning/npi-project-planning.component";


const ScrollToTop = () => {
    const {pathname} = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

export const Routes = () => {
    const Private = ({children, permission}: { children: React.ReactNode, permission?: Permission }): JSX.Element | null => {
        const {authenticated, loading, user} = useAuth();
        const {hasPermission} = usePermission();
        const accessDenied = !!permission && !!user && !hasPermission(permission.profile);

        if (loading) {
            return <Loading open={loading}/>;
        }
        if (!authenticated) {
            return <Navigate to="/login"/>;
        }
        if (accessDenied) {
            return <Navigate to={URLS.ACCESS_DENIED}/>;
        }
        return <LayoutComponent>{children}</LayoutComponent>;
    };

    const adminPermission: Permission = {profile: [Profile.ADMIN]};
    const operacionalPermission: Permission = {profile: [Profile.OPERACIONAL]};
    const suportePermission: Permission = {profile: [Profile.SUPORTE]};
    const allPermission: Permission = {profile: [Profile.ADMIN, Profile.OPERACIONAL, Profile.SUPORTE]};
    const adminAndOperacionalPermission: Permission = {profile: [Profile.ADMIN, Profile.OPERACIONAL]};
    const adminAndSuportePermission: Permission = {profile: [Profile.ADMIN, Profile.SUPORTE]};

    return (
        <Router>
            <ScrollToTop/>
            <AuthProvider>
                <PermissionsProvider>
                    <Switch>
                    <Route path={URLS.DENIED_ACCESS} element={<DeniedAccess/>}/>
                    <Route path={URLS.LOGIN} element={<LoginComponent/>}/>
                    <Route path={URLS.INDEX} element={<Private><HomeComponent/></Private>}/>
                    <Route path={URLS.HOME} element={<Private><HomeComponent/></Private>}/>
                    <Route path={URLS.CATEGORY} element={<Private permission={adminPermission}><CategoryComponent/></Private>}></Route>
                    <Route path={URLS.PROJECTS} element={<Private permission={adminPermission}><ProjectsComponent/></Private>}></Route>
                    <Route path={URLS.FT_BOM_GROUPS} element={<Private permission={adminAndOperacionalPermission}><FtBomGroupComponent/></Private>}></Route>
                    <Route path={URLS.PO_MONITORING} element={<Private permission={adminPermission}><PoMonitoringComponent/></Private>}></Route>
                    <Route path={URLS.PO_MONITORING_DETAIL} element={<Private permission={adminPermission}><PoMonitoringDetailComponent/></Private>}></Route>
                    <Route path={URLS.IMPORT_PO} element={<Private permission={adminPermission}><ImportPoComponent/></Private>}></Route>
                    <Route path={URLS.IMPORT_AMORTIZATION} element={<Private permission={adminPermission}><ImportAmortizationComponent/></Private>}></Route>
                    <Route path={URLS.IMPORT_RELATED_COST} element={<Private permission={adminPermission}><ImportRelatedCostComponent/></Private>}></Route>
                    <Route path={URLS.IMPORT_INTEGRATION_ROUTINES} element={<Private permission={adminPermission}><ImportIntegrationRoutinesComponent/></Private>}></Route>
                    <Route path={URLS.IMPORT_CONFIRMATION_MONITORING_PO} element={<Private permission={adminPermission}><ImportConfirmationMonitoringPo/></Private>}></Route>

                    <Route path={URLS.REPORT_ASSET_CONTROL} element={<Private permission={adminPermission}><AssetControlComponent/></Private>}></Route>
                    <Route path={URLS.REPORT_ASSET_CONTROL_INVOICE} element={<Private permission={adminPermission}><AssetControlInvoiceComponent/></Private>}></Route>
                    <Route path={URLS.REPORT_RELATED_COST} element={<Private permission={adminPermission}><RelatedCostComponent/></Private>}></Route>
                    <Route path={URLS.REPORT_COST_ALLOCATION_PROJECT_CATEGORY} element={<Private permission={adminPermission}><CostAllocationProjectCategoryComponent/></Private>}></Route>
                    <Route path={URLS.REPORT_STOCK_AND_MATERIAL_OUTPUT} element={<Private permission={allPermission}><StockAndMaterialOutputComponent/></Private>}></Route>
                    <Route path={URLS.REPORT_FTB_GENERAL} element={<Private permission={allPermission}><FTBReportComponent/></Private>}></Route>

                    <Route path={URLS.DASHBOARD} element={<Private permission={adminPermission}><DashboardComponent/></Private>}></Route>
                    <Route path={URLS.FTB_MOUNT_JIGA_RMA} element={<Private permission={allPermission}><MountJigaRmaComponent/></Private>}></Route>
                    <Route path={URLS.REPLENISHMENT} element={<Private permission={allPermission}><ReplenishmentComponent/></Private>}></Route>
                    <Route path={URLS.CLIENT} element={<Private permission={adminPermission}><ClientComponent/></Private>}></Route>
                    <Route path={URLS.USER} element={<Private permission={adminPermission}><UserComponent/></Private>}></Route>
                    <Route path={URLS.USER + "/:action"} element={<Private permission={adminPermission}><UserItemComponent/></Private>}></Route>
                    <Route path={URLS.PENDING_INVOICES } element={<Private permission={adminPermission}><PendingNreInvoiceReportComponent/></Private>}></Route>
                    <Route path={URLS.NPI_HOME} element={<Private><NpiHomeComponent/></Private>}></Route>
                    <Route path={URLS.NPI_DASHBOARD} element={<Private><NpiDashboardComponent/></Private>}></Route>
                    <Route path={URLS.NPI_CHECKLIST} element={<Private><NpiChecklistComponent/></Private>}></Route>

                    <Route path={URLS.NPI_TEMPLATE} element={<Private><NpiTemplateComponent/></Private>}></Route>
                    <Route path={URLS.NPI_TEMPLATE_DETAIL} element={<Private><NpiTemplateDetailComponent/></Private>}></Route>
                    <Route path={URLS.NPI_CALENDAR} element={<Private><NpiCalendarComponent/></Private>}></Route>
                    <Route path={URLS.NPI_PROJECT_PLANNING} element={<Private><NpiProjectPlanningComponent/></Private>}></Route>

                    <Route path={URLS.MAPLOG_HOME} element={<Private><MapLogHomeComponent/></Private>}></Route>
                    <Route path={URLS.ACCESS_DENIED} element={<Private><AccessDeniedComponent/></Private>}></Route>


                    </Switch>
                </PermissionsProvider>
            </AuthProvider>
        </Router>
    );
};
